import { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'

import {
  HALF_ANIMATION_DURATION,
  POPUP_QUESTIONS,
  POPUP_QUESTION_KEYS,
  PROGRESS_MIDPOINTS,
  reviewAvatar,
  reviewStars,
} from '~/pages/communication/preparePlan/libs/components/PreparePlanCommunication/constants'
import { Styles as S } from '~/pages/communication/preparePlan/libs/components/PreparePlanCommunication/styles'
import ProgressAnalyzer from '~/shared/components/ProgressAnalyzer'
import { useDelayedContinue } from '~/shared/hooks/useDelayedContinue'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { useSegmentAnimation } from '~/shared/hooks/useSegmentAnimation'
import { eventLogger } from '~/shared/services/eventLogger'
import { TPageProps } from '~/shared/types/commonTypes'

const PROGRESS_COLOR = '#096ef9'

const PreparePlanCommunication = ({ nextPagePath, pageId }: TPageProps) => {
  const [activePopup, setActivePopup] = useState<0 | 1 | 2 | 3 | null>(null)

  const { handleChange } = useDelayedContinue()
  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question: 'Creating your personalized learning plan',
  })
  const goToNextPage = () => handleChange('', handleContinue)

  const {
    displayValue: d1,
    slowValue: s1,
    isFinished: fin1,
    start: start1,
    resume: resume1,
  } = useSegmentAnimation({
    halfDuration: HALF_ANIMATION_DURATION,
    midpoint: PROGRESS_MIDPOINTS[0],
    onMidpoint: () => setActivePopup(0),
  })

  const {
    displayValue: d2,
    slowValue: s2,
    isFinished: fin2,
    start: start2,
    resume: resume2,
  } = useSegmentAnimation({
    halfDuration: HALF_ANIMATION_DURATION,
    midpoint: PROGRESS_MIDPOINTS[1],
    onMidpoint: () => setActivePopup(1),
  })

  const {
    displayValue: d3,
    slowValue: s3,
    isFinished: fin3,
    start: start3,
    resume: resume3,
  } = useSegmentAnimation({
    halfDuration: HALF_ANIMATION_DURATION,
    midpoint: PROGRESS_MIDPOINTS[2],
    onMidpoint: () => setActivePopup(2),
  })

  const {
    displayValue: d4,
    slowValue: s4,
    start: start4,
    resume: resume4,
  } = useSegmentAnimation({
    halfDuration: HALF_ANIMATION_DURATION,
    midpoint: PROGRESS_MIDPOINTS[3],
    onMidpoint: () => setActivePopup(3),
    onComplete: goToNextPage,
  })

  useEffect(() => {
    document.documentElement.style.setProperty('--header-bg', '#EFF5F7')
    return () => {
      document.documentElement.style.removeProperty('--header-bg')
    }
  }, [])

  useEffect(() => {
    eventLogger.logGenerationPlanScreenShow()
    start1()
  }, [start1])

  useEffect(() => {
    if (fin1) start2()
  }, [fin1, start2])

  useEffect(() => {
    if (fin2) start3()
  }, [fin2, start3])

  useEffect(() => {
    if (fin3) start4()
  }, [fin3, start4])

  const resumeFns = [resume1, resume2, resume3, resume4]

  const handleAnswer = (answer: string) => {
    const popup = activePopup
    setActivePopup(null)
    if (popup !== null) {
      eventLogger.logQuestion({
        question: POPUP_QUESTIONS[popup],
        answers: answer,
        pageName: pageId,
      })
      eventLogger.logGenerationPlanQuestionCompleted(
        POPUP_QUESTIONS[popup],
        answer,
      )
      resumeFns[popup]()
    }
  }

  return (
    <S.Root>
      <S.StyledContainer>
        <S.Title>
          <Trans
            ns="communication"
            i18nKey="onboarding.communicationPreparePlan.title"
          />
        </S.Title>

        <S.ProgressContainer>
          <ProgressAnalyzer color={PROGRESS_COLOR} value={d1} numberValue={s1}>
            <Trans
              ns="communication"
              i18nKey="onboarding.communicationPreparePlan.goalsAligning"
            />
          </ProgressAnalyzer>

          <ProgressAnalyzer color={PROGRESS_COLOR} value={d2} numberValue={s2}>
            <Trans
              ns="communication"
              i18nKey="onboarding.communicationPreparePlan.settingSkills"
            />
          </ProgressAnalyzer>

          <ProgressAnalyzer color={PROGRESS_COLOR} value={d3} numberValue={s3}>
            <Trans
              ns="communication"
              i18nKey="onboarding.communicationPreparePlan.pickingContent"
            />
          </ProgressAnalyzer>

          <ProgressAnalyzer color={PROGRESS_COLOR} value={d4} numberValue={s4}>
            <Trans
              ns="communication"
              i18nKey="onboarding.communicationPreparePlan.prioritizingChallenges"
            />
          </ProgressAnalyzer>
        </S.ProgressContainer>

        <S.EnjoyTitle>
          <Trans
            ns="communication"
            i18nKey="onboarding.communicationPreparePlan.trustedBy"
          />
        </S.EnjoyTitle>

        <S.Review>
          <S.ReviewHeader>
            <S.ReviewAvatar src={reviewAvatar} alt="reviewer" />

            <S.ReviewNameBlock>
              <S.ReviewAuthor>
                <Trans
                  ns="communication"
                  i18nKey="onboarding.communicationPreparePlan.reviewAuthor"
                />
              </S.ReviewAuthor>
              <S.ReviewStars src={reviewStars} alt="5 stars" />
            </S.ReviewNameBlock>
          </S.ReviewHeader>

          <S.ReviewText>
            <Trans
              ns="communication"
              i18nKey="onboarding.communicationPreparePlan.reviewText"
            />
          </S.ReviewText>
        </S.Review>
      </S.StyledContainer>

      {activePopup !== null && (
        <S.Overlay>
          <S.PopupCard>
            <S.PopupQuestion>
              <Trans
                ns="communication"
                i18nKey={`onboarding.communicationPreparePlan.popup.${POPUP_QUESTION_KEYS[activePopup]}`}
              />
            </S.PopupQuestion>

            <S.PopupButtonWrapper>
              <S.PopupButtonNo type="button" onClick={() => handleAnswer('no')}>
                <Trans i18nKey="actions.no" />
              </S.PopupButtonNo>

              <S.PopupButtonYes
                type="button"
                onClick={() => handleAnswer('yes')}
              >
                <Trans i18nKey="actions.yes" />
              </S.PopupButtonYes>
            </S.PopupButtonWrapper>
          </S.PopupCard>
        </S.Overlay>
      )}
    </S.Root>
  )
}

export default PreparePlanCommunication
